<template>
  <div class="fip-header">
    <div>
      <div class="fip-header-background show-from-large">
        <picture
          class="landscape">
          <source
            v-for="(picture, index) in imageData.pictures"
            :key="index"
            :srcset="picture.source"
            :media="picture.media"
            :type="picture.type">
          <img
            ref="fipImg"
            :src="imageUrl"
            :alt="item.title"
            width="1280px"
            height="auto"
            class="fip-background">
        </picture>
      </div>
      <div class="btn-back-container">
        <wptv-arrow-link
          id="back-button"
          :back="true"
          :dark-mode="true"
          :label="backButtonLabel"
          :to="parentNode.fullPath"
          data-wptv="banniere-retour"
          @click.native="goToParentNode" />
      </div>

      <fip-preview
        ref="fipPreview"
        :item="item"
        :can-be-bookmarked="canBeBookmarked"
        :pre-title="preTitle"
        class="fip-preview" />

      <fip-header-actions
        :alert="alert"
        :item="item"
        :seasons="seasons || []"
        :order-info="orderInfo || {}"
        :has-ticket-video="hasTicketVideo"
        :definitions-by-quality="definitionsByQuality"
        :selected-quality="selectedQuality"
        :is-prospect="isProspect"
        :banner-definition="bannerDefinition || {}"
        :can-be-bookmarked="canBeBookmarked"
        class="fip-header-actions"
        @on-switch-quality="onSwitchQuality"
        @on-buy-rent="onClickButtonBuyOrRent" />

      <fip-navigation :item="item" />
    </div>
  </div>
</template>

<script>
import _get from 'lodash/get'
import _groupBy from 'lodash/groupBy'
import _has from 'lodash/has'
import _isEmpty from 'lodash/isEmpty'
import _orderBy from 'lodash/orderBy'
import { mapGetters } from 'vuex'
import { profileHasAccess } from '../../../../middleware/checkups'
import { setItemImageData } from '../../../shared/imageData.utils'
import { WptvArrowLink } from '@wptv/components/ui/arrowLink'
import API_CONSTANTS from '../../../../api.constants'
import FipHeaderActions from './fipHeaderActions/FipHeaderActions'
import FipNavigation from './fipNavigation/FipNavigation'
import FipPreview from './fipPreview/FipPreview'
import FipUtils from '../fip.utils'
import flexivod from '../../../shared/flexivod'

export default {
  name: 'FipHeader',
  components: {
    WptvArrowLink,
    FipPreview,
    FipNavigation,
    FipHeaderActions
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    promoType: {
      type: String,
      required: true
    },
    seasons: {
      type: Array,
      default: null
    },
    redirectTo: {
      type: Object,
      required: true
    },
    canBeBookmarked: {
      type: Boolean,
      default: true
    },
    preTitle: {
      type: String,
      default: ''
    }
  },
  data () {
    const imageData = setItemImageData(this.item, {}, this.$config)
    return {
      selectedQuality: 'SD',
      definition: null,
      speedTestLaunched: false,
      imageData
    }
  },
  computed: {
    ...mapGetters({
      userDiscounts: 'user/getUserDiscounts',
      profile: 'core/getProfile',
      alert: 'alerts/getAlert',
      parentNavigationNode: 'navik/getParentNavigationNode',
      getSelectedQuality: 'fip/getSelectedQuality'
    }),
    orderInfo () {
      return _get(this.item, 'orderInfo', {})
    },
    hasTicketVideo () { // Season must check if all episodes are bought are not, in that case, in that case Season is bought
      switch (this.item.type) {
        case API_CONSTANTS.RESOURCE_TYPES.SEASON:
          return this.item.episodes.every(episode => episode.orderInfo)
        default:
          return !_isEmpty(this.orderInfo)
      }
    },
    definitionsByQuality () {
      const infoPromos = flexivod.infoPromo(this.userDiscounts, '', this.item.definitions, this.promoType)
      return _groupBy(_orderBy(infoPromos, ['usage'], ['desc']), 'quality')
    },
    selectedDefinition () {
      return _get(this.definitionsByQuality, this.selectedQuality, [])[0] || {}
    },
    bannerDefinition () {
      return (_get(this.selectedDefinition, 'name') || _get(this.selectedDefinition, 'description')) ? this.selectedDefinition : null
    },
    isProspect () {
      return FipUtils.isProgramProspected(this.item)
    },
    imageUrl () {
      return this.getImageFromImageData()
    },
    backButtonLabel () {
      return this.parentNode?.name ? this.$t('general.backTo', { target: this.parentNode.name }) : this.$t('general.back')
    },
    parentNode () {
      if (this.parentNavigationNode && !this.parentNavigationNode.fullPath.match(/^\/payment\//)) {
        return this.parentNavigationNode
      } else {
        const parentNode = this.getParentNode()

        return parentNode ?? { fullPath: '/', name: this.$t('navik.home') }
      }
    }
  },
  updated () {
    if (!this.definitionsByQuality.SD && this.definitionsByQuality.HD) {
      this.selectedQuality = 'HD'
    }
  },
  mounted () {
    if (this.getSelectedQuality && this.definitionsByQuality.HD) {
      this.selectedQuality = this.getSelectedQuality
    }
    if (profileHasAccess(this.profile) && this.isProspect) {
      this.$store.dispatch('alerts/getAlertById', _get(this.$route, 'params.id', ''))
    }
  },
  methods: {
    getImageFromImageData () {
      return this.imageData?.pictures?.[0]?.source || this.imageData.image.url
    },
    onSwitchQuality (quality) {
      this.$store.commit('fip/setSelectedQuality', quality)
      this.selectedQuality = quality
    },
    onClickButtonBuyOrRent (definition) {
      this.$router.push({
        name: 'payment',
        params: {
          definition,
          item: this.item,
          promoName: _get(this.bannerDefinition, 'name', null)
        }
      })
    },
    goToParentNode () {
      this.sendStats(this.$stats.fip.onClickBackButton(this.parentNodeLink))
    },
    getParentNode () {
      if (_has(this.item, 'packages') && this.item.packages.length > 0) {
        const name = _get(this.item, 'packages[0].title', null)
        const id = _get(this.item, 'packages[0].id', null)

        if (name === null || id === null) {
          return null
        }

        return {
          name: this.$t('navik.pack', { name }),
          fullPath: `/collection-film-serie/${id}`
        }
      }

      if (_has(this.item, 'seasonInfo')) {
        const name = _get(this.item, 'seasonInfo.title', null)
        const id = _get(this.item, 'seasonInfo.id', null)

        if (name === null || id === null) {
          return null
        }

        return {
          name: this.$t('navik.season', { name }),
          fullPath: `/serie/${id}`
        }
      }
      return null
    }
  }
}
</script>

<style lang="scss" scoped>
.fip-header {
  position: relative;
  background: $black;
  @include mediaQueries('margin-bottom', (large: 30px));

  > div {
    position: relative;
    overflow: hidden;
  }

  .fip-header-background {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    opacity: 0.2;
    display: flex;
    align-items: center;

    picture {
      min-width: 100%;
    }
    img {
      min-width: 100%;
      filter: blur(20px);
    }
  }

  // Back Button position
  .btn-back-container {
    height: 50px;
    top: 0;
    left: 0;
    z-index: 2;
    max-width: 1280px;
    margin: auto;
    @include mediaQueries('position', (xsmall: absolute, large: relative));
    @include mediaQueries('padding', (xsmall: 5px 15px 0, medium: 5px 27px 0, large: 5px 55px 0));
    text-overflow: ellipsis;

    a {
      max-width: 1280px;
    }
  }

  .fip-header-actions {
    max-width: 1280px;
    margin: auto;
    display: flex;
    @include mediaQueries('flex-direction', (xsmall: column, xlarge: row));
    @include mediaQueries('align-items', (xsmall: flex-start, xlarge: center));
    @include mediaQueries('padding-left', (xsmall: 15px, medium: 27px, large: 55px));
    @include mediaQueries('padding-right', (xsmall: 15px, medium: 27px, large: 55px));
  }

  .fip-navigation {
    position: absolute;
    max-width: 1280px;
    @include mediaQueries('left', (xlarge: 50%));
    @include mediaQueries('transform', (xlarge: translateX(-50%)));
    @include mediaQueries('top', (xsmall: 20%, small: 30%, large: 155px, xlarge: 180px));
  }

  .progress-bar {
    width: 100%;

    > div {
      margin-bottom: 15px;
      width: 120px;
      @include mediaQueries(width, (medium: 195px));
    }
  }

  // Dropdown saison passe dessous input coupon en petite résolution
  ::v-deep .custom-options {
    z-index: 999;
  }
  ::v-deep .error-modal a {
    text-decoration: underline;
  }
}
</style>
